'use client';
import React, { useState } from 'react';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';

import { FacebookIcon } from '../../icons/FacebookIcon';
import { LInkedinIcon } from '../../icons/LinkedinIcon';
import { PintrestIcon } from '../../icons/PintrestIcon';
import { TwitterIcon } from '../../icons/TwitterIcon';

export default function PremiumSocialShare({ title, url, media }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-8 flex flex-col items-center">
      <h3 className="mb-4 text-xl font-semibold text-gray-700 dark:text-white">Share Article</h3>

      <div className="mb-6 flex items-center justify-center">
        <div className="relative flex w-full max-w-lg items-center">
          <input
            type="text"
            value={url}
            readOnly
            aria-label="Article share link"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 pr-16 text-gray-700 transition focus:ring-2 focus:ring-indigo-300 focus:outline-none"
          />
          <button
            onClick={copyToClipboard}
            className="absolute right-2 rounded-md p-2 transition hover:bg-gray-100"
            aria-label={copied ? 'Link copied' : 'Copy link'}
          >
            {copied ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-green-500"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-500"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-flow-col gap-4">
        <FacebookShareButton url={url} quote={title} hashtag={title} aria-label="Share on Facebook">
          <div className="transform rounded-full bg-gradient-to-br from-blue-500 to-blue-700 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <FacebookIcon />
          </div>
        </FacebookShareButton>

        <TwitterShareButton url={url} title={title} aria-label="Share on Twitter">
          <div className="transform rounded-full bg-gradient-to-br from-blue-400 to-blue-500 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <TwitterIcon />
          </div>
        </TwitterShareButton>

        <LinkedinShareButton
          url={url}
          title={title}
          summary={title}
          source={url}
          aria-label="Share on LinkedIn"
        >
          <div className="transform rounded-full bg-gradient-to-br from-blue-600 to-blue-800 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <LInkedinIcon />
          </div>
        </LinkedinShareButton>

        <PinterestShareButton
          url={url}
          media={media}
          description={title}
          aria-label="Share on Pinterest"
        >
          <div className="transform rounded-full bg-gradient-to-br from-red-500 to-red-700 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <PintrestIcon />
          </div>
        </PinterestShareButton>

        <WhatsappShareButton url={url} title={title} aria-label="Share on WhatsApp">
          <div className="transform rounded-full bg-gradient-to-br from-green-400 to-green-600 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="white"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
        </WhatsappShareButton>

        <EmailShareButton url={url} subject={title} body={title} aria-label="Share via Email">
          <div className="transform rounded-full bg-gradient-to-br from-gray-500 to-gray-700 p-3 text-white shadow-lg transition duration-300 hover:scale-110 hover:shadow-xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
        </EmailShareButton>
      </div>
    </div>
  );
}
